/**
 * Shared presentational field components for the collab plan display: the reveal
 * anchor, removed-item ghosts, prior-value bands, collapsible sections/rows, and the
 * blur-committing editable fields. Consumed by both the structure view
 * (CollabPlanDisplay) and the Test Instructions view.
 */
import { useContext, useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { AutoResizingTextarea } from "../../AutoResizingTextarea";
import {
  FIELD_HIGHLIGHT_CLASS,
  PlanFieldDiffContext,
  previousValueFor,
  useCommittableDraft,
  useFieldReveal,
  useRevealOpen,
} from "./planReveal";

/**
 * Reveal anchor for fields rendered inline in the plan (not through
 * EditableDetailField): hosts the scroll target and flash highlight.
 */
export const FieldRevealAnchor = ({
  fieldKey,
  className,
  children,
}: {
  fieldKey: string;
  className?: string;
  children: React.ReactNode;
}) => {
  const { ref, isHighlighted } = useFieldReveal(fieldKey);
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-md transition-colors duration-500",
        isHighlighted && FIELD_HIGHLIGHT_CLASS,
        className,
      )}
    >
      {children}
    </div>
  );
};

/** Badge marking an in-place ghost of an item the latest revision removed. */
export const RemovedBadge = () => (
  <span className="rounded-sm bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-600">
    Removed
  </span>
);

/** Read-only detail line inside a removed-column ghost: label plus the old
 *  value struck through in red. Hidden when the field was empty. */
export const GhostDetailField = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) =>
  !value || !value.trim() ? null : (
    <div className="text-xs leading-relaxed">
      <span className="font-medium text-red-400">{label}: </span>
      <span className="whitespace-pre-wrap break-words text-red-400/80 line-through">
        {value}
      </span>
    </div>
  );

/**
 * Wraps an editable input with the prior value struck through above it so the
 * old and new text read as one field box; pass-through when there is no prior
 * value. The child input should drop its own border/ring (the wrapper owns
 * them) via e.g. `border-0 shadow-none focus-visible:ring-0`.
 */
export const WithPreviousValue = ({
  previousValue,
  children,
}: {
  previousValue?: string;
  children: React.ReactNode;
}) =>
  previousValue === undefined ? (
    <>{children}</>
  ) : (
    <div className="overflow-hidden rounded-md border border-stone-200 bg-white focus-within:ring-1 focus-within:ring-stone-400">
      <PreviousValueBand value={previousValue} className="text-sm" />
      {children}
    </div>
  );

/** The struck-through prior value rendered as the top band of a field box. */
export const PreviousValueBand = ({
  value,
  className,
}: {
  value: string;
  className?: string;
}) => (
  <p
    className={cn(
      "whitespace-pre-wrap break-words border-b border-stone-100 px-3 py-1.5 leading-relaxed text-red-400 line-through",
      className,
    )}
  >
    {value}
  </p>
);

export interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  onAdd?: () => void;
  showAdd?: boolean;
  disabled?: boolean;
  /** Diff-key prefixes contained in this section; a reveal targeting one of
   *  them opens the section. */
  revealPrefixes?: string[];
}

export const CollapsibleSection = ({
  title,
  defaultOpen = true,
  children,
  onAdd,
  showAdd = false,
  disabled = false,
  revealPrefixes,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  useRevealOpen(revealPrefixes, setIsOpen);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 text-sm font-medium text-stone-800 hover:text-stone-900"
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-stone-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-stone-400" />
          )}
          {title}
        </button>
        {showAdd && onAdd && (
          <button
            type="button"
            onClick={onAdd}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        )}
      </div>
      {isOpen && children}
    </section>
  );
};

/** Labeled form field: bold label above the input, matching the mockup. */
export const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    {children}
  </div>
);

/**
 * A chevron-toggled disclosure row: a clickable header (arbitrary content) that
 * shows/hides its children. Used in the instructions view for both the
 * per-testing-table blocks and the individual column cards.
 */
export const CollapsibleRow = ({
  header,
  children,
  defaultOpen = false,
  className,
  action,
  revealPrefixes,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  /** Optional trailing control (e.g. a remove button) shown beside the header.
   * Rendered as a sibling of the toggle button to avoid nesting <button>s. */
  action?: React.ReactNode;
  /** Diff-key prefixes contained in this row; a reveal targeting one of them
   *  opens the row. */
  revealPrefixes?: string[];
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  useRevealOpen(revealPrefixes, setIsOpen);

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-stone-400" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-stone-400" />
          )}
          <span className="min-w-0 flex-1">{header}</span>
        </button>
        {action}
      </div>
      {isOpen && <div className="mt-2">{children}</div>}
    </div>
  );
};

/**
 * One column-detail field. Read-only mode shows a labelled inline value (hidden
 * when empty); editable mode shows an auto-sizing textarea that grows with its
 * text and commits on blur. Local draft avoids emitting a patch per keystroke.
 */
export const EditableDetailField = ({
  label,
  value,
  editable,
  placeholder,
  onCommit,
  fieldKey,
  suppressReveal = false,
  singleLine = false,
}: {
  label: string;
  value: string | null | undefined;
  editable: boolean;
  placeholder?: string;
  onCommit: (value: string) => void;
  /** Diff-key of this field (see `fieldKeys`); omitted for fields the plan
   *  differ doesn't cover. */
  fieldKey?: string;
  /** When an ancestor already flashes this same key on reveal, suppress this
   *  field's own flash so the highlight isn't doubled — the diff still uses
   *  fieldKey. */
  suppressReveal?: boolean;
  /** Render a fixed-height single-line input instead of the auto-growing
   *  textarea — for short fields like names/titles. */
  singleLine?: boolean;
}) => {
  const diffByKey = useContext(PlanFieldDiffContext);
  const previousValue = previousValueFor(
    fieldKey ? diffByKey.get(fieldKey) : undefined,
    value,
  );
  const { ref, isHighlighted } = useFieldReveal(
    suppressReveal ? undefined : fieldKey,
  );
  const { draft, setDraft, onFocus, endEditing } = useCommittableDraft(
    value ?? "",
  );

  if (!editable) {
    const hasValue = !!value && !!value.trim();
    // A revision that emptied the field still needs its old text shown.
    if (!hasValue && previousValue === undefined) return null;
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-md text-xs leading-relaxed transition-colors duration-500",
          isHighlighted && FIELD_HIGHLIGHT_CLASS,
        )}
      >
        <span className="font-medium text-stone-600">{label}: </span>
        {previousValue !== undefined && (
          <span className="block whitespace-pre-wrap break-words text-red-400 line-through">
            {previousValue}
          </span>
        )}
        {hasValue && (
          <span className="whitespace-pre-wrap text-stone-500">{value}</span>
        )}
      </div>
    );
  }

  const commit = () => {
    endEditing();
    if (draft.trim() !== (value ?? "").trim()) onCommit(draft.trim());
  };

  // A single-line field (a name/title) uses a fixed-height input rather than the
  // auto-growing textarea, which floors at ~80px and looks oversized for one line.
  const control = singleLine ? (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={onFocus}
      onBlur={commit}
      placeholder={placeholder}
      className={cn(
        "h-8 text-xs",
        previousValue !== undefined &&
          "border-0 shadow-none focus-visible:ring-0",
      )}
    />
  ) : (
    <AutoResizingTextarea
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={onFocus}
      onBlur={commit}
      minHeight={36}
      // High cap so long guidance grows to fit its text rather than becoming
      // an inner scroll box; the panel itself scrolls if needed.
      maxHeight={2000}
      placeholder={placeholder}
      className={cn(
        "text-xs",
        previousValue !== undefined &&
          "border-0 shadow-none focus-visible:ring-0",
      )}
    />
  );

  return (
    <div
      ref={ref}
      className={cn(
        "space-y-1 rounded-md transition-colors duration-500",
        isHighlighted && FIELD_HIGHLIGHT_CLASS,
      )}
    >
      {label && (
        <Label className="text-[11px] font-medium text-stone-500">
          {label}
        </Label>
      )}
      {previousValue !== undefined ? (
        <div className="overflow-hidden rounded-md border border-stone-200 bg-white focus-within:ring-1 focus-within:ring-stone-400">
          <PreviousValueBand value={previousValue} className="text-xs" />
          {control}
        </div>
      ) : (
        control
      )}
    </div>
  );
};

/**
 * Inline-editable attribute reference (the "A1" code). Commits on blur/Enter and sends the
 * new code with the attribute's id, so the rename matches its row and re-letters the
 * testing tables rather than being read as delete-and-recreate.
 */
export const EditableAttributeReference = ({
  value,
  onCommit,
}: {
  value: string;
  onCommit: (value: string) => void;
}) => {
  const { draft, setDraft, onFocus, endEditing } = useCommittableDraft(value);

  const commit = () => {
    endEditing();
    const next = draft.trim();
    if (next && next !== value) onCommit(next);
    else setDraft(value);
  };

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={onFocus}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      aria-label="Attribute reference"
      className="h-6 w-24 text-sm font-semibold text-stone-800"
    />
  );
};
